/**
 * Person Entity Types
 *
 * Person is the central identity model. Both Guest and Staff are thin role
 * records that point to a Person. All personal data lives here.
 * See docs/api/BACKEND.md § Person for source of truth.
 */

export type DocumentType = 'passport' | 'id_card' | 'driving_license' | 'other' | '';

export type PersonRole = 'guest' | 'staff' | 'none';
export type PersonGender = 'male' | 'female' | 'non_binary' | 'other' | 'prefer_not_to_say' | '';

export interface PersonRoleHistory {
  id: string;
  role: 'guest' | 'staff';
  startedAt: string;
  endedAt: string | null;
  notes: string;
}

export type PersonEmailType = 'account' | 'personal' | 'work' | 'emergency' | 'other';
export type PersonPhoneType = 'mobile' | 'home' | 'work' | 'emergency' | 'other';

export interface PersonEmail {
  id: string;
  email: string;
  type: PersonEmailType;
  isPrimary: boolean;
  isVerified: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PersonPhone {
  id: string;
  phone: string;
  phoneCountryIso: string;
  phoneCountryCode: string;
  type: PersonPhoneType;
  isPrimary: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PersonPhoneFormData {
  id?: string;         // present when editing an existing phone
  phone: string;
  phoneCountryIso: string;
  phoneCountryCode: string;
  type: PersonPhoneType;
  isPrimary: boolean;
}

export interface PersonEmailFormData {
  id?: string;         // present when editing an existing email
  email: string;
  type: PersonEmailType;
  isPrimary: boolean;
}

export interface PersonDocument {
  id: string;
  countryIso: string;
  documentType: DocumentType;
  documentNumber: string;
  expirationDate: string | null;
  isPrimary: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Person {
  id: string;
  personProfileId: string | null;
  locationId: string | null;
  name: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  avatarUrl: string;
  gender: PersonGender;
  documentType: DocumentType;
  documentNumber: string;
  documentExpiration: string | null;
  nationality: string;
  nationalityIso: string;
  birthDate: string | null;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  languages: string[];
  notes: string;
  allergies: string[];
  userId: string | null;
  /**
   * Tri-state. `undefined` means UNKNOWN — the payload asserted neither
   * `has_user` (list serializers) nor `user_id` (detail serializers), so
   * nothing is known. Only `false` is a definite "no login": test for it
   * explicitly before offering to create one.
   */
  hasUser?: boolean;
  staffId: string | null;
  guestId: string | null;
  primaryEmail: string;
  primaryPhone: PersonPhone | null;
  primaryDocument: PersonDocument | null;
  emails: PersonEmail[];
  phones: PersonPhone[];
  documents: PersonDocument[];
  currentRole: PersonRole;
  isActive: boolean;
  roleHistory: PersonRoleHistory[];
  createdAt: string;
  updatedAt: string;
}

export interface PersonFormData {
  firstName: string;
  lastName: string;
  email: string;
  displayName?: string;
  emails?: PersonEmailFormData[];
  phones?: PersonPhoneFormData[];
  nationality?: string;
  documentType?: DocumentType;
  documentCountryIso?: string;
  documentNumber?: string;
  documentExpiration?: string | null;
  birthDate?: string;
  address?: string;
  city?: string;
  country?: string;
  postalCode?: string;
  languages?: string[];
  allergies?: string[];
  notes?: string;
  locationId?: string;
  gender?: PersonGender;
  nationalityIso?: string;
  /** @deprecated Use phones array instead */
  phone?: string;
  /** @deprecated Use phones array instead */
  phoneCountryIso?: string;
  /** @deprecated Use phones array instead */
  phoneCountryCode?: string;
}


// ── Check-in identity match ──────────────────────────────────────────────────
// Front-desk identity recovery: surface existing people who may be the guest
// being checked in, so staff link to the real person instead of duplicating.

export type CheckinMatchConfidence = 'strong' | 'possible';
export type CheckinMatchReason = 'wristband' | 'document' | 'email' | 'name_dob' | 'name';

export interface PersonStayHistoryItem {
  arrivalDate: string | null;
  departureDate: string | null;
  room: string | null;
  bookingReference: string | null;
  status: string;
}

/**
 * A person's staff stint. Person↔Staff is one-to-one, so this is present at
 * most once. There is no termination date; the "exit" is conveyed by
 * status (inactive / on_leave) + isActive.
 */
export interface PersonStaffHistory {
  position: string | null;
  department: string | null;
  location: string | null;
  employmentType: string;
  status: string;
  hireDate: string | null;
  isActive: boolean;
}

export interface PersonStayHistory {
  pastStays: number;
  lastStayDate: string | null;
  lastRoom: string | null;
  recent: PersonStayHistoryItem[];
  /** Staff stint, if this person was ever staff. Tenant-wide identity. */
  staff: PersonStaffHistory | null;
}

export interface CheckinMatchInput {
  nfcUid?: string;
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  email?: string;
  documentNumber?: string;
}

export interface CheckinMatchCandidate {
  person: Person;
  confidence: CheckinMatchConfidence;
  matchReasons: CheckinMatchReason[];
  history: PersonStayHistory;
}

/**
 * Bare-name overflow: the typed name alone matched more people than the
 * backend will surface (>25). No candidates are returned — the UI shows the
 * count and asks for a discriminating field (birth date, document, email).
 */
export interface CheckinMatchOverflow {
  reason: 'name';
  count: number;
}

export interface CheckinMatchResult {
  /** At most 5, strong tier first, most recently active first within a tier. */
  candidates: CheckinMatchCandidate[];
  /** Matches found before the cap — drives "+N more" in the UI. */
  total: number;
  overflow: CheckinMatchOverflow | null;
}

/** Split a single `name` string into firstName / lastName (frontend convenience). */
export function splitName(name: string): { firstName: string; lastName: string } {
  const spaceIndex = name.indexOf(' ');
  return {
    firstName: spaceIndex > -1 ? name.slice(0, spaceIndex) : name,
    lastName: spaceIndex > -1 ? name.slice(spaceIndex + 1) : '',
  };
}

/** Join firstName + lastName into a single `name` string for the backend. */
export function joinName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim();
}

/**
 * Resolve the `hasUser` tri-state from whichever field the payload carries.
 *
 * List serializers ship `has_user`, detail serializers ship `user_id`, and the
 * backend derives the former from the latter (`bool(obj.user_id)`), so either
 * key present — `user_id` even when its value is null — is a definite answer.
 * A payload carrying neither asserted nothing, and must stay `undefined`:
 * inferring `false` there invents a "no login" nobody claimed, and callers act
 * on it by offering to create a second one.
 */
function readHasUser(b: Record<string, unknown>): boolean | undefined {
  const flag = b.has_user ?? b.hasUser;
  if (typeof flag === 'boolean') return flag;
  if ('user_id' in b) return Boolean(b.user_id);
  if ('userId' in b) return Boolean(b.userId);
  return undefined;
}

/** Map a raw backend person object (snake_case) to the Person interface. */
export function mapBackendPerson(b: Record<string, unknown>): Person {
  const read = <T>(keys: string[], fallback: T): T => {
    for (const key of keys) {
      const value = (b as Record<string, unknown>)[key];
      if (value !== undefined && value !== null) return value as T;
    }
    return fallback;
  };

  const roleHistoryRaw = read<Record<string, unknown>[]>(['roleHistory', 'role_history'], []);
  const roleHistory: PersonRoleHistory[] = roleHistoryRaw.map((rh) => ({
    id: (rh.id as string) ?? '',
    role: ((rh.role as string) ?? 'guest') as PersonRoleHistory['role'],
    startedAt: (rh.started_at as string) ?? (rh.startedAt as string) ?? '',
    endedAt: (rh.ended_at as string | null) ?? (rh.endedAt as string | null) ?? null,
    notes: (rh.notes as string) ?? '',
  }));
  const phonesRaw = read<Record<string, unknown>[]>(['phones'], []);
  const phones: PersonPhone[] = phonesRaw.map((p) => ({
    id: (p.id as string) ?? '',
    phone: (p.phone as string) ?? '',
    phoneCountryIso: (p.phone_country_iso as string) ?? (p.phoneCountryIso as string) ?? '',
    phoneCountryCode: (p.phone_country_code as string) ?? (p.phoneCountryCode as string) ?? '',
    type: ((p.type as string) ?? 'mobile') as PersonPhoneType,
    isPrimary: Boolean((p.is_primary as boolean) ?? (p.isPrimary as boolean) ?? false),
    isActive: Boolean((p.is_active as boolean) ?? (p.isActive as boolean) ?? true),
    createdAt: (p.created_at as string) ?? (p.createdAt as string) ?? '',
    updatedAt: (p.updated_at as string) ?? (p.updatedAt as string) ?? '',
  }));
  const primaryPhoneRaw = read<Record<string, unknown> | null>(['primary_phone', 'primaryPhone'], null);
  const primaryPhone: PersonPhone | null = primaryPhoneRaw ? {
    id: '',
    phone: (primaryPhoneRaw.phone as string) ?? '',
    phoneCountryIso: (primaryPhoneRaw.phone_country_iso as string) ?? (primaryPhoneRaw.phoneCountryIso as string) ?? '',
    phoneCountryCode: (primaryPhoneRaw.phone_country_code as string) ?? (primaryPhoneRaw.phoneCountryCode as string) ?? '',
    type: ((primaryPhoneRaw.type as string) ?? 'mobile') as PersonPhoneType,
    isPrimary: true,
    isActive: true,
    createdAt: '',
    updatedAt: '',
  } : (phones.find((p) => p.isPrimary && p.isActive) ?? phones.find((p) => p.isActive) ?? null);

  const emailsRaw = read<Record<string, unknown>[]>(['emails'], []);
  const emails: PersonEmail[] = emailsRaw.map((email) => ({
    id: (email.id as string) ?? '',
    email: (email.email as string) ?? '',
    type: ((email.type as string) ?? 'personal') as PersonEmailType,
    isPrimary: Boolean((email.is_primary as boolean) ?? (email.isPrimary as boolean) ?? false),
    isVerified: Boolean((email.is_verified as boolean) ?? (email.isVerified as boolean) ?? false),
    isActive: Boolean((email.is_active as boolean) ?? (email.isActive as boolean) ?? true),
    createdAt: (email.created_at as string) ?? (email.createdAt as string) ?? '',
    updatedAt: (email.updated_at as string) ?? (email.updatedAt as string) ?? '',
  }));
  const documentsRaw = read<Record<string, unknown>[]>(['documents'], []);
  const documents: PersonDocument[] = documentsRaw.map((document) => ({
    id: (document.id as string) ?? '',
    countryIso: (document.country_iso as string) ?? (document.countryIso as string) ?? '',
    documentType: ((document.document_type as string) ?? (document.documentType as string) ?? '') as DocumentType,
    documentNumber: (document.document_number as string) ?? (document.documentNumber as string) ?? '',
    expirationDate: (document.expiration_date as string | null) ?? (document.expirationDate as string | null) ?? null,
    isPrimary: Boolean((document.is_primary as boolean) ?? (document.isPrimary as boolean) ?? false),
    isActive: Boolean((document.is_active as boolean) ?? (document.isActive as boolean) ?? true),
    createdAt: (document.created_at as string) ?? (document.createdAt as string) ?? '',
    updatedAt: (document.updated_at as string) ?? (document.updatedAt as string) ?? '',
  }));
  const primaryDocumentRaw = read<Record<string, unknown> | null>(['primary_document', 'primaryDocument'], null);
  const primaryDocument = primaryDocumentRaw ? {
    id: (primaryDocumentRaw.id as string) ?? '',
    countryIso: (primaryDocumentRaw.country_iso as string) ?? (primaryDocumentRaw.countryIso as string) ?? '',
    documentType: ((primaryDocumentRaw.document_type as string) ?? (primaryDocumentRaw.documentType as string) ?? '') as DocumentType,
    documentNumber: (primaryDocumentRaw.document_number as string) ?? (primaryDocumentRaw.documentNumber as string) ?? '',
    expirationDate: (primaryDocumentRaw.expiration_date as string | null) ?? (primaryDocumentRaw.expirationDate as string | null) ?? null,
    isPrimary: Boolean((primaryDocumentRaw.is_primary as boolean) ?? (primaryDocumentRaw.isPrimary as boolean) ?? true),
    isActive: Boolean((primaryDocumentRaw.is_active as boolean) ?? (primaryDocumentRaw.isActive as boolean) ?? true),
    createdAt: (primaryDocumentRaw.created_at as string) ?? (primaryDocumentRaw.createdAt as string) ?? '',
    updatedAt: (primaryDocumentRaw.updated_at as string) ?? (primaryDocumentRaw.updatedAt as string) ?? '',
  } satisfies PersonDocument : null;
  const split = splitName(read<string>(['name'], ''));
  const firstName = read<string>(['first_name', 'firstName'], split.firstName);
  const lastName = read<string>(['last_name', 'lastName'], split.lastName);
  const displayName = read<string>(['display_name', 'displayName'], '');
  const baseEmail = read<string>(['email'], '');
  const primaryEmail = read<string>(['primary_email', 'primaryEmail'], emails.find((email) => email.isPrimary)?.email ?? baseEmail);

  return {
    id: read<string>(['id'], ''),
    personProfileId: read<string | null>(['person_profile_id', 'personProfileId'], null),
    locationId: read<string | null>(['locationId', 'location'], null),
    name: read<string>(['name'], ''),
    firstName,
    lastName,
    displayName,
    email: baseEmail,
    avatarUrl: read<string>(['avatarUrl', 'avatar_url'], ''),
    gender: read<PersonGender>(['gender'], ''),
    documentType: read<DocumentType>(['documentType', 'document_type'], ''),
    documentNumber: read<string>(['documentNumber', 'document_number'], ''),
    documentExpiration: read<string | null>(['documentExpiration', 'document_expiration'], null),
    nationality: read<string>(['nationality'], ''),
    nationalityIso: read<string>(['nationality_iso', 'nationalityIso'], ''),
    birthDate: read<string | null>(['birthDate', 'birth_date'], null),
    address: read<string>(['address'], ''),
    city: read<string>(['city'], ''),
    country: read<string>(['country'], ''),
    postalCode: read<string>(['postalCode', 'postal_code'], ''),
    languages: read<string[]>(['languages'], []),
    notes: read<string>(['notes'], ''),
    allergies: read<string[]>(['allergies'], []),
    userId: read<string | null>(['userId', 'user_id'], null),
    hasUser: readHasUser(b),
    staffId: read<string | null>(['staff_id', 'staffId'], null),
    guestId: read<string | null>(['guest_id', 'guestId'], null),
    primaryEmail,
    primaryPhone,
    primaryDocument,
    emails,
    phones,
    documents,
    currentRole: read<PersonRole>(['currentRole', 'current_role'], 'none'),
    isActive: read<boolean>(['isActive', 'is_active'], true),
    roleHistory,
    createdAt: read<string>(['createdAt', 'created_at'], ''),
    updatedAt: read<string>(['updatedAt', 'updated_at'], ''),
  };
}
